import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { IoAddOutline } from 'react-icons/io5';
import { useHistory, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  ActionButton,
  Alert,
  BackButton,
  DataStatus,
  FilterButton,
  InputSearch,
  Pagination,
  Table,
  Td,
  Th,
} from 'components';
import { TransferSisaProduksiApi } from 'api';
import { DateConvert, TableNumber } from 'utilities';
import { ModalFilterRealisasiProduksi } from './Components';
import { withDropdownTransferSisaProduksiContextProvider } from './Contexts';
import { useSessionStorage } from 'hooks';

const PreformTransferSisaProduksiList = ({ setNavbarTitle }) => {
  const history = useHistory();
  const location = useLocation();
  const paginationKey = `${location.pathname}_pagination`;

  const [isPageLoading, setIsPageLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useSessionStorage(paginationKey, {
    q: '',
    page: '1',
    per_page: '10',
    total_page: '1',
    data_count: '0',

    active: false,
    tgl_job_order_start: undefined,
    tgl_job_order_end: undefined,
    tgl_realisasi_produksi_start: undefined,
    tgl_realisasi_produksi_end: undefined,
    id_barang_jadi: undefined,
    id_customer: undefined,
    id_proyek: undefined,
  });
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    variant: 'primary',
    text: '',
  });

  const getInitialData = () => {
    setIsPageLoading(true);

    const query = {
      q: pagination.q,
      page: pagination.page,
      per_page: pagination.per_page,
      tgl_realisasi_produksi_start: pagination.tgl_realisasi_produksi_start,
      tgl_realisasi_produksi_end: pagination.tgl_realisasi_produksi_end,
      tgl_job_order_start: pagination.tgl_job_order_start,
      tgl_job_order_end: pagination.tgl_job_order_end,
      id_barang_jadi: pagination.id_barang_jadi,
      id_customer: pagination.id_customer,
      id_proyek: pagination.id_proyek,
    };

    TransferSisaProduksiApi.preform(query)
      .then(({ data }) => {
        setData(data.data);
        setPagination({
          ...pagination,
          data_count: data.data_count,
          total_page: data.total_page,
        });
      })
      .catch((err) =>
        setAlertConfig({
          show: true,
          variant: 'danger',
          text: `Data gagal dimuat ${err.response.data.message}`,
        })
      )
      .finally(() => setIsPageLoading(false));
  };

  const checkAlert = () => {
    const locationState = location.state;

    if (locationState?.alert) {
      setAlertConfig(locationState.alert);
    }
  };

  useEffect(() => {
    setNavbarTitle('Transfer Sisa Produksi');
    getInitialData();
    checkAlert();

    return () => {
      setIsPageLoading(false);
    };
  }, [
    setNavbarTitle,
    pagination.q,
    pagination.page,
    pagination.per_page,

    pagination.tgl_realisasi_produksi_start,
    pagination.tgl_realisasi_produksi_end,
    pagination.tgl_job_order_start,
    pagination.tgl_job_order_end,
    pagination.id_barang_jadi,
    pagination.id_customer,
    pagination.id_proyek,
  ]);

  const TableContent = () => {
    if (!data || data.length < 1) {
      return <DataStatus text="Tidak ada data" />;
    }

    return (
      <>
        <div className="mt-2" style={{ fontSize: '.9rem' }}>
          List Data Realisasi Produksi
        </div>
        <Table>
          <thead>
            <tr>
              <Th>No.</Th>
              <Th>Informasi Realisasi Produksi</Th>
              <Th>Informasi Job Order</Th>
              <Th>Customer</Th>
              <Th>Proyek</Th>
              <Th>Item Job Order</Th>
              <Th>Qty. Job Order</Th>
              <Th>Qty.Realisasi</Th>
              <Th>Aksi</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((val, index) => (
              <tr key={index}>
                <Td textCenter>
                  {TableNumber(pagination.page, pagination.per_page, index)}
                </Td>
                <Td>
                  <div>
                    {val.tgl_realisasi_produksi
                      ? DateConvert(new Date(val.tgl_realisasi_produksi))
                          .defaultDMY
                      : '-'}
                  </div>
                  <div>{val.no_realisasi_produksi ?? '-'}</div>
                </Td>
                <Td>
                  <div>
                    {val.tgl_job_order
                      ? DateConvert(new Date(val.tgl_job_order)).defaultDMY
                      : '-'}
                  </div>
                  <div>{val.no_job_order ?? '-'}</div>
                </Td>
                <Td>{val.nama_customer ?? '-'}</Td>
                <Td>{val.nama_proyek ?? '-'}</Td>
                <Td>{val.nama_barang_jadi ?? '-'}</Td>
                <Td>{`${parseFloat(val.qty_rap ?? 0)} ${val.nama_satuan}`} </Td>
                <Td>
                  {`${parseFloat(val.qty_realisasi ?? 0)} ${val.nama_satuan}`}{' '}
                </Td>
                <Td>
                  <ActionButton
                    size="sm"
                    onClick={() =>
                      history.push(
                        `/transaksi/transfer-sisa-produksi/tambah/${val.id_realisasi_produksi}`
                      )
                    }
                  >
                    <IoAddOutline />
                  </ActionButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Pagination
          dataLength={pagination.per_page}
          dataNumber={
            pagination.page * pagination.per_page - pagination.per_page + 1
          }
          dataPage={
            pagination.data_count < pagination.per_page
              ? pagination.data_count
              : pagination.page * pagination.per_page
          }
          dataCount={pagination.data_count}
          currentPage={pagination.page}
          totalPage={pagination.total_page}
          onPaginationChange={({ selected }) =>
            setPagination({
              ...pagination,
              page: selected + 1,
            })
          }
          onDataLengthChange={(e) =>
            setPagination({
              ...pagination,
              page: 1,
              per_page: e.target.value,
            })
          }
        />
      </>
    );
  };

  return (
    <>
      <Row className="mb-1">
        <Col className="d-flex flex-row justify-space-between align-items-center">
          <InputSearch
            defaultValue={pagination.q}
            onChange={debounce(
              (e) =>
                setPagination((prv) => ({
                  ...prv,
                  q: e.target.value,
                  page: 1,
                })),
              1500
            )}
          />
          <FilterButton
            active={pagination.active}
            onClick={() => setShowFilter((prev) => !prev)}
          />
        </Col>
        <Col className="d-flex justify-content-end">
          <BackButton size="md" onClick={() => history.goBack()} />
        </Col>
      </Row>

      <Alert
        show={alertConfig.show}
        variant={alertConfig.variant}
        text={alertConfig.text}
        showCloseButton={true}
        onClose={() =>
          setAlertConfig({
            ...alertConfig,
            show: false,
          })
        }
      />

      {isPageLoading ? (
        <DataStatus loading={true} text="Memuat data . . ." />
      ) : (
        <TableContent />
      )}

      <ModalFilterRealisasiProduksi
        show={showFilter}
        setShow={setShowFilter}
        filter={pagination}
        setFilter={setPagination}
      />
    </>
  );
};

export default withDropdownTransferSisaProduksiContextProvider(
  PreformTransferSisaProduksiList
);
